import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Subject, Subscription} from 'rxjs';
import {AttachmentModel} from '@Common/widgets/general/attachments/interfaces/attachment.model';
import {Upload} from '@Common/widgets/general/attachments/interfaces/upload.interface';
import {ObjectGeneric} from '@Common/widgets/general/object-generic-django.interface';
import {ApplicationEnum} from '@Common/widgets/general/attachments/interfaces/application.enum';
import {SnackBarToastyService, typeToast} from '@Common/widgets/general/snack-bar';

/**
 * Gestion de téléversement de fichiers
 */
@Component({
  selector: 'p8-attachments',
  templateUrl: './attachments.component.html',
  styleUrls: ['./attachments.component.scss']
})
export class AttachmentsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() simpleView = true;
  /**
   * Objet rattaché au fichier, objet FK générique django
   * object_id est la clé FK de FICHIER vers OBJET désigné (par exemple Fichier ---- 1 CommentaireGeneric (objet), ici CommentaireGeneric)
   * content_type est le type du modèle / objet raccroché de la table django_content_type : exemple CommentaireGeneric object_id 366 et content_type = 267
   */
  @Input() object_id: number;
  @Input() content_type: number;
  /**
   * Titre et sous-titre de la carte quand simpleView = false
   */
  @Input() title = '';
  @Input() subTitle = '';
  /**
   * L'application concernée par le téléversement (par exemple : demat, dar, helpdesk, ater, etc)
   * Attention, important, cela déterminera le répertoire de stockage des fichiers
   * dans attachments/application/YYYY/MM/DD/
   */
  @Input() application: ApplicationEnum = ApplicationEnum.default;
  /**
   * Permet d'ajouter un répertoire à application, ex. attachments/application/subDir/YYYY/MM/DD/
   */
  @Input() subDir = '';
  @Input() labelJoin = 'parcourir';
  @Input() buttonIconAlign: 'start' | 'end' = 'end';
  @Input() buttonSelectIcon: 'attach_file' | 'file_upload' | string = 'attach_file';
  @Input() colorButton: 'accent' | 'warn' | 'primary' | 'default' = 'default';
  @Input() typeButton: 'icon' | 'label' = 'icon';
  @Input() disabled: boolean;
  /**
   * liste des extensions acceptées, de la forme ext1,ext2,ext3
   * ie: .pdf,.doc,.xls
   */
  @Input() accept: string;
  @Input() maxFiles = 1;
  @Input() maxFileSize = 5000000;

  /**
   * Déclenchement upload fichier pour l'objet concerné (comment, responsedemat, ticket, etc) donc après que l'objet ait été créé
   * Doit contenir object_id, content_type
   */
  @Input()
  connector: Subject<AttachmentModel>;

  /**
   * Ràz des fichiers et indicateurs
   */
  @Input()
  reset: Subject<any>;
  /**
   * Etat de l'uploading, optionnel
   */
  @Input()
  uploading$: Subject<boolean>;
  /**
   * A la fin du téléversement des fichiers, émission
   */
  @Output()
  onFinished: EventEmitter<AttachmentModel[]> = new EventEmitter<AttachmentModel[]>();

  /**
   * Retourne un message si erreur utilisé par le fileName pour l'instant
   */
  @Output()
  onProgress: EventEmitter<Upload[]> = new EventEmitter<Upload[]>();

  /**
   * A la mise à jour de la liste des fichiers à téléverser lors du traitement
   */
  @Output()
  onUpdatedFiles: EventEmitter<AttachmentModel[]> = new EventEmitter<AttachmentModel[]>();

  attachments: AttachmentModel[] = [];
  attachmentsUploaded: AttachmentModel[] = [];
  subscriptionSignal: Subscription;
  resetSignal: Subscription;
  MAX_FILE_LENGTH = 100; // max_length par défaut en Django 4.2 porté à 100, avec le path complet
  /**
   * Pour déclenchement téléversement fichier
   */
  launcher: Subject<ObjectGeneric> = new Subject<ObjectGeneric>();
  collector: number;
  files: FileList;
  upload: Upload;
  uploadTotal: Upload = {state: 'IN_PROGRESS', progress: 0};
  uploadFiles: Upload[];

  /**
   * fileInput hidden fichier
   * static
   * true to resolve query results before change detection runs,
   * false to resolve after change detection. Defaults to false.
   */
  @ViewChild('fileInput', { static: false }) fileInput;

  constructor(private snackSvc: SnackBarToastyService) {
  }

  ngOnInit() {
    if (this.connector) {
      this.subscriptionSignal = this.connector.subscribe(objectToAttach => this._startUpload(objectToAttach));
    }
    if (this.reset) {
      this.resetSignal = this.reset.subscribe(() => this.resetData());
    }
  }
  trackByFn(index, item) {
    return index;
  }
  canShowButton() {
    return ! this.uploadFiles || (this.uploadTotal && this.uploadTotal.state === 'DONE');
  }
  isButtonDisabled() {
    return this.maxFiles === this.attachments?.length;
  }
  /**
   * Ajout de la liste des fichiers qui seront à téléverser par l'API
   * @param event
   */
  prepareAttachmentsToUpload(event: Event) {
    this.files = this.fileInput.nativeElement.files;
    if (this._checkFilesAccepted(this.files)) {
      for (let i = 0; i < this.files.length; i++) {
        if (this.files.item(i).name.length > this.MAX_FILE_LENGTH) {
          this.snackSvc.toasty('Nom du fichier', `Le nom de votre fichier est trop long (${this.MAX_FILE_LENGTH} caractères Maximum)`,
            typeToast.error);
        } else {
          this.attachments.push(this.getAttachmentFile(this.files.item(i)));
          this.onUpdatedFiles.emit(this.attachments);
        }
      }
    } else {

    }
  }
  private _truncateFileName(file) {
    let _filenameOrigin = null;
    if (file.name) {
      _filenameOrigin = file.name.length <= this.MAX_FILE_LENGTH ? file.name : file.name.substring(0, this.MAX_FILE_LENGTH);
    }
    return _filenameOrigin;
  }
  /**
   * Mapping file => AttachmentModel file
   * @param file
   */
  getAttachmentFile(file: any): AttachmentModel {
      return <AttachmentModel> {
        file: file,
        name: this._truncateFileName(file),
      };
  }

  /**
   * Event sur la fin de l'upload du composant fils p8-attachment-upload
   * @param attachment
   */
  collectFiles(attachment) {
    this.collector++;
    if (attachment) {
      this.attachmentsUploaded.push(attachment);
    }
    if (this.collector === this.attachments.length) {
      this._announceParent();
    }
  }
  /**
   * Vérification des types de fichiers acceptés selon this.accept
   * @param {FileList} files
   * @private
   */
  private _checkFilesAccepted(files: FileList) {
    if (this.accept) {
      for (let i = 0; i < files.length; i++) {
        const file: File = files[i];
        const ext = file.name.split('.').pop();
        if (!ext || this.accept.indexOf(ext) === -1) {
          return false;
        }
      }
    }
    return true;
  }
  /**
   * Event au composant parent pour annoncer que le fichier a fini d'être téléversé
   * @private
   */
  private _announceParent() {
    this.onFinished.emit(this.attachmentsUploaded);
    if (this.uploading$) {
      this.uploading$.next(false);
    }
    this.resetData();
  }

  /**
   * Calcul de la progression total avec l'ensemble des fichiers en cours de téléversement
   * @private
   */
  private _calculateProgress() {
    let progress = 0;
    this.uploadFiles.forEach((_upload: Upload, _idx: number) => progress += _upload.progress);
    this.uploadTotal.state = this.uploadFiles.every((_upload: Upload) => _upload.state === 'DONE') ? 'DONE' : 'PENDING';
    this.uploadTotal.progress = progress / this.attachments.length;
  }

  /**
   * Lancement des téléversements
   * @param {AttachmentModel} objectToAttach : objet à uploader
   * @private
   */
  private _startUpload(objectToAttach: AttachmentModel) {
    this.collector = 0;
    if (this.attachments && this.attachments.length > 0) {
      objectToAttach.object_id = objectToAttach.object_id || this.object_id;
      objectToAttach.content_type = objectToAttach.content_type || this.content_type;
      objectToAttach.sub_dir = objectToAttach.sub_dir || '';
      objectToAttach.application = this.application;
      objectToAttach.filename_origin = objectToAttach.filename_origin || null;
      this.launcher.next(objectToAttach);
      if (this.uploading$) {
        this.uploading$.next(true);
      }
    } else {
      this._announceParent();
    }
  }

  /**
   * Event de la progression du téléversement du composant fils app-piece-jointe-upload
   * Calcul total de la progression de l'ensemble des fichiers en cours de téléversement
   * @param upload
   * @param idx
   */
  progressFile(upload: Upload, idx) {
    if (!this.uploadFiles) {
      this.uploadFiles = new Array(this.attachments.length);
    }
    this.uploadFiles[idx] = upload;
    this._calculateProgress();
    this.onProgress.emit(this.uploadFiles);
  }
  /**
   * Suppression d'un fichier dans la liste à téléverser
   * @param event
   */
  childDestroy(event) {
    const idx = this.attachments.indexOf(event);
    if (idx !== -1) {
      this.attachments.splice(idx, 1);
      this.onUpdatedFiles.emit(this.attachments);
    }
  }
  /**
   * Lancement hors Input() du téléversement des fichiers par l'API
   */
  launchUpload() {
    this.launcher.next(null);
  }

  ngOnDestroy(): void {
    if (this.subscriptionSignal) {
      this.subscriptionSignal.unsubscribe();
    }
    if (this.reset) {
      this.resetSignal.unsubscribe();
    }
  }
  private resetData() {
    this.attachments = [];
    this.attachmentsUploaded = [];
    this.files = null;
    this.uploadTotal = {state: 'IN_PROGRESS', progress: 0};
    this.uploadFiles = null;
    this.onUpdatedFiles.emit(this.attachments);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes?.object_id?.currentValue) &&
        (changes?.content_type?.currentValue)) {
      console.log('changed object_id / content_type');
      console.log(changes?.object_id?.currentValue);
      console.log(changes?.content_type?.currentValue);
    }
  }
}
